/* Cart Drawer — local state with live background sync.
   Local `state` is the source of truth for the UI; +/- clicks mutate it
   and the DOM updates instantly. A debounced background sync keeps the
   Shopify cart in lock-step so refresh / external pages always see the
   same state. Per-card spinners show wherever local ≠ server, so the
   user gets feedback without the whole drawer dimming.
   Frequency / plan changes do a full rebuild (clear main lines + re-add
   under the new selling_plan); pure quantity changes use /cart/update.js
   with line indices for a single-RTT atomic update. */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer || drawer.dataset.cdInit) return;
  // Activation gate — only the subscription PDP template lights up the new
  // drawer. On every other page the section renders (so its editor panel
  // is accessible globally) but nothing is wired and UpCart stays in
  // charge of cart interactions. Matches multiple signals because Shopify's
  // template object surfaces inconsistently across preview URL variants.
  var body = document.body;
  var templateAttr = body && body.getAttribute('data-template') || '';
  var suffixAttr = body && body.getAttribute('data-template-suffix') || '';
  var isSubscriptionTemplate = (
    // Global setting — "Use new cart drawer" toggle in theme settings.
    // When on, the drawer activates site-wide and UpCart is hidden via CSS.
    (body && body.classList.contains('use-new-cart-drawer')) ||
    suffixAttr === 'pdp-subscription' ||
    templateAttr === 'page.pdp-subscription' ||
    (body && body.classList.contains('template-page-pdp-subscription')) ||
    (body && body.classList.contains('template-suffix-pdp-subscription'))
  );
  if (!isSubscriptionTemplate) return;
  drawer.dataset.cdInit = '1';

  // ===== Buy-Once-discount page lock (dynamic) =====
  // Any page that renders a buybox (product_discount_bar) with "Disable Buy Once
  // volume discount" ON pins the whole cart into "Disable Buy-Once discounts"
  // once the shopper adds from it. Detected live from the DOM — no manual page
  // list. The flag is stored client-side so it persists as they browse other
  // pages, and clears when the cart empties. renderTotals() ORs
  // buyonceLockActive() into the flag.
  var BUYONCE_LOCK_KEY = 'pg_buyonce_lock';
  // True when a NON-drawer buybox on the current page has disable-buyonce on.
  // Excludes the cart drawer's own bar (which mirrors the cart, not the page).
  function isBuyonceLockContext() {
    var bars = document.querySelectorAll('[data-pg-discount-bar][data-pg-disable-buyonce-discounts="1"]');
    for (var i = 0; i < bars.length; i++) {
      if (bars[i].closest && !bars[i].closest('#cart-drawer')) return true;
    }
    return false;
  }
  function buyonceLockActive() { try { return localStorage.getItem(BUYONCE_LOCK_KEY) === '1'; } catch (e) { return false; } }
  function setBuyonceLock() { try { localStorage.setItem(BUYONCE_LOCK_KEY, '1'); } catch (e) {} }
  function clearBuyonceLock() { try { localStorage.removeItem(BUYONCE_LOCK_KEY); } catch (e) {} }

  // ===== A/B save-tiers override lock =====
  // When a page's buybox uses the "Save tiers override" setting, it stamps the bar
  // with data-pg-save-tiers-override="1" and its data-pg-save-tiers holds the override
  // tier prices. Once the shopper adds from such a page, latch those tiers so the
  // whole cart prices with them (mirrors the buy-once lock). Cleared when cart empties.
  var SAVE_TIERS_OVERRIDE_KEY = 'pg_save_tiers_override';
  function saveTiersOverrideContext() {
    var bars = document.querySelectorAll('[data-pg-discount-bar][data-pg-save-tiers-override="1"]');
    for (var i = 0; i < bars.length; i++) {
      if (bars[i].closest && !bars[i].closest('#cart-drawer')) {
        var t = bars[i].getAttribute('data-pg-save-tiers');
        if (t) return t;
      }
    }
    return null;
  }
  function getSaveTiersOverride() { try { return localStorage.getItem(SAVE_TIERS_OVERRIDE_KEY); } catch (e) { return null; } }
  function setSaveTiersOverride(json) { try { if (json) localStorage.setItem(SAVE_TIERS_OVERRIDE_KEY, json); } catch (e) {} }
  function clearSaveTiersOverride() { try { localStorage.removeItem(SAVE_TIERS_OVERRIDE_KEY); } catch (e) {} }
  // Latch both locks on a successful add.
  function latchCartLocks() {
    if (isBuyonceLockContext()) setBuyonceLock();
    var ov = saveTiersOverrideContext();
    if (ov) setSaveTiersOverride(ov);
  }

  // Latch the flag the moment an add-to-cart succeeds while a disable-buyonce
  // buybox is on the page — however the add is performed (theme $s flow, custom
  // fetch, or XHR). The context is re-checked at add time, so it's always live.
  (function installBuyonceLockLatch() {
    try {
      var _lockFetch = window.fetch;
      window.fetch = function () {
        var u = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
        var p = _lockFetch.apply(this, arguments);
        if (/\/cart\/add(\.js|$|\?)/.test(u) && p && p.then) { p.then(function (r) { if (!r || r.ok !== false) latchCartLocks(); }); }
        return p;
      };
    } catch (e) {}
    try {
      var _lockOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (m, url) {
        try { if (/\/cart\/add/.test(url)) this.addEventListener('load', function () { latchCartLocks(); }); } catch (e2) {}
        return _lockOpen.apply(this, arguments);
      };
    } catch (e) {}
  })();

  var initData = window.cartDrawerData || {};
  // Support one OR many main products (combined flavor picker, shared tier rail).
  var MAIN_PRODUCT_IDS = (initData.productIds && initData.productIds.length)
    ? initData.productIds.map(Number)
    : (initData.productId ? [Number(initData.productId)] : []);

// Subscription qty rules: qty<2 → no sub allowed; qty>=2 → subscribe available.
  var SUB_QTY_RULES = { minSub: 2 };

  // Virtual subscribe-discount tiers — match the Shopify volume discount
  // that auto-applies server-side. Plans themselves carry no pct anymore
  // (one SKIO selling plan group, frequencies only), so we compute the
  // displayed savings here so the UI stays in sync with what Shopify will
  // actually charge at checkout.
  var SUB_DISCOUNT_TIERS = [
    { minQty: 3, pct: 25 },
    { minQty: 2, pct: 20 }
  ];
  function getVirtualSubDiscountPct(qty) {
    for (var i = 0; i < SUB_DISCOUNT_TIERS.length; i++) {
      if (qty >= SUB_DISCOUNT_TIERS[i].minQty) return SUB_DISCOUNT_TIERS[i].pct;
    }
    return 0;
  }

  // Debounce window between user click and the server sync. Long enough that
  // rapid + clicks coalesce into one round-trip, short enough that the user
  // sees the cart settle quickly.
  var SYNC_DEBOUNCE_MS = 350;

  // ========== State (local source of truth) ==========
  var state = {
    main: {},              // { variantId: qty }
    other: [],             // [{ key, variantId, qty, sellingPlan, properties, ... }]
    freqMode: 'subscribe', // 'buy_once' | 'subscribe' — default subscribe; applyRules drops to buy_once below minSub
    planId: null,
    // Set true the moment the user explicitly clicks a freq card.
    // Until then, applyRules auto-promotes back to 'subscribe' whenever qty
    // crosses minSub, so the subscribe-first default actually sticks.
    userPickedFreq: false
  };
  var serverCart = { items: [], item_count: 0, total_price: 0 };

  // Sync state
  var syncTimer = null;
  var syncing = false;
  var pendingSync = false;
  var checkingOut = false;

  // Cart-wide auto-reconcile guard. When a subscribed line lands alongside a
  // one-time main line (e.g. Product A subscribed then Product B added one-time
  // from its PDP), we auto-unify the whole cart to subscription. reconcileSig
  // fingerprints the server state so we only retry a FAILING reconcile a few
  // times, then give up — otherwise an un-subscribable line would spin forever.
  var reconcileSig = null;
  var reconcileTries = 0;
  var RECONCILE_MAX = 3;

  // ========== Money ==========
  function formatMoney(cents) {
    var n = Math.max(0, Math.round(cents || 0));
    var hasFraction = n % 100 !== 0;
    var dollars = (n / 100).toFixed(2);
    var parts = dollars.split('.');
    var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + (hasFraction ? (intPart + '.' + parts[1]) : intPart);
  }

  // ========== Drawer open/close ==========
  // No-op body scroll lock. The drawer's full-viewport fixed-position overlay
  // visually covers the page, so locking body scroll is unnecessary — and
  // every time we did, leftover state on <html>/<body> made the page
  // unscrollable for some users. Drop the lock entirely; the visual block
  // is enough.
  function lockScroll() { /* intentionally empty */ }
  function unlockScroll() {
    // Still runs on close() as a defensive sweep: clears any scroll lock
    // that some OTHER code (UpCart, theme modal, Shopify pixel, etc.) may
    // have applied around the same time. Inline 'auto' beats class rules.
    document.body.classList.remove('cd-scroll-locked');
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
  }
  function open() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockScroll();
    hideOverlay();
  }
  var scrollLockGuard = null;
  function close() {
    // Move focus OFF the close button BEFORE we set aria-hidden, otherwise
    // Chrome refuses to apply aria-hidden ("Blocked aria-hidden on an element
    // because its descendant retained focus") and the page is left in a
    // half-trapped state.
    try {
      var activeEl = document.activeElement;
      if (activeEl && drawer.contains(activeEl) && typeof activeEl.blur === 'function') {
        activeEl.blur();
      }
    } catch (e) {}
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    unlockScroll();
    hideOverlay();
    clearAllScrollLocks('close immediate');
    // Forcibly hide the theme's loading overlay element inline — it's
    // position:fixed full-screen at z-index:999999 (HIGHER than our drawer),
    // so if `.show-loading-overlay` lingers on <html> the loader covers the
    // page and the user perceives "the page is frozen". Inline display:none
    // wins over any class rule.
    try {
      var loaderEl = document.getElementById('loading-overlay');
      if (loaderEl) loaderEl.style.display = 'none';
    } catch (e) {}
    // Reactive safety net — if anything (UpCart's app embed, Shopify
    // customer-event analytics, etc.) re-applies a scroll lock within 5s
    // of close, revert it immediately. Observer is reactive (not polling)
    // so it doesn't generate the [Violation] long-task warnings the timer
    // cleanups did.
    if (typeof MutationObserver === 'undefined') return;
    if (scrollLockGuard) { scrollLockGuard.disconnect(); scrollLockGuard = null; }
    scrollLockGuard = new MutationObserver(function () {
      if (drawer.classList.contains('is-open')) return;
      clearAllScrollLocks('mutation-observer');
    });
    scrollLockGuard.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });
    scrollLockGuard.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
    setTimeout(function () {
      if (scrollLockGuard) { scrollLockGuard.disconnect(); scrollLockGuard = null; }
    }, 3000);
  }
  function clearAllScrollLocks(label) {
    try {
      var html = document.documentElement;
      var body = document.body;
      var hadLoading = html.classList.contains('show-loading-overlay');
      var hadOverlay = html.classList.contains('show-overlay');
      var hadCdLock = body.classList.contains('cd-scroll-locked');
      var htmlInline = html.style.overflow;
      var bodyInline = body.style.overflow;
      // Nothing to do — bail BEFORE touching any attribute so we don't
      // trigger the MutationObserver guard and cascade into a self-firing
      // infinite loop that freezes the main thread (Page Unresponsive).
      var lockedHtml = htmlInline === 'hidden';
      var lockedBody = bodyInline === 'hidden';
      if (!hadLoading && !hadOverlay && !hadCdLock && !lockedHtml && !lockedBody) return;
      // Temporarily disconnect the MutationObserver while we mutate so our
      // own writes don't re-trigger us.
      var hadGuard = !!scrollLockGuard;
      if (hadGuard) scrollLockGuard.disconnect();
      if (hadLoading) html.classList.remove('show-loading-overlay');
      if (hadOverlay) html.classList.remove('show-overlay');
      if (hadCdLock) body.classList.remove('cd-scroll-locked');
      // Only WRITE style when actually locked; otherwise the no-op write
      // still mutates the style attribute and re-fires the observer.
      if (lockedHtml) html.style.overflow = 'auto';
      if (lockedBody) body.style.overflow = 'auto';
      if (hadGuard && scrollLockGuard) {
        scrollLockGuard.observe(html, { attributes: true, attributeFilter: ['class', 'style'] });
        scrollLockGuard.observe(body, { attributes: true, attributeFilter: ['class', 'style'] });
      }
      console.log('[CS] scroll-lock cleared (' + label + ')', {
        'html.show-loading-overlay': hadLoading,
        'html.show-overlay': hadOverlay,
        'body.cd-scroll-locked': hadCdLock,
        'html.style.overflow': htmlInline,
        'body.style.overflow': bodyInline
      });
    } catch (e) {}
  }
  function hideOverlay() {
    if (window.$s && typeof window.$s.hideLoadingOverlay === 'function') {
      try { window.$s.hideLoadingOverlay(); } catch (e) {}
    }
  }

  // ========== Bar helpers ==========
  function getBar() { return drawer.querySelector('[data-pg-discount-bar]'); }
  function parseAttr(el, name) {
    try { var raw = el && el.getAttribute(name); return raw ? JSON.parse(raw) : null; }
    catch (e) { return null; }
  }
  function getSkioPlans() {
    var bar = getBar();
    return bar ? (parseAttr(bar, 'data-pg-skio-plans') || []) : [];
  }
  function getSaveTiers() {
    // A latched A/B override wins so the cart prices with the same tiers the page used.
    var ov = getSaveTiersOverride();
    if (ov) { try { var parsed = JSON.parse(ov); if (parsed) return parsed; } catch (e) {} }
    var bar = getBar();
    return bar ? parseAttr(bar, 'data-pg-save-tiers') : null;
  }
  function tierPriceFor(saveTiers, qty) {
    if (!saveTiers) return null;
    var idx = qty <= 1 ? 0 : qty === 2 ? 1 : qty === 3 ? 2 : 3;
    var price = saveTiers[idx];
    if (price == null && idx === 3) price = saveTiers[2];
    if (price == null && idx === 2) price = saveTiers[1];
    if (price == null && idx === 1) price = saveTiers[0];
    return typeof price === 'number' ? price : null;
  }
  function getMainTotalQty() {
    var sum = 0;
    Object.keys(state.main).forEach(function (k) { sum += state.main[k] || 0; });
    return sum;
  }
  function getOtherTotalQty() {
    return state.other.reduce(function (s, it) { return s + (it.qty || 0); }, 0);
  }
  function isMainItem(item) {
    return MAIN_PRODUCT_IDS.indexOf(Number(item.product_id)) !== -1;
  }
  // All frequency plans are visible whenever subscribe is allowed. The pct
  // gating is gone — every frequency now points at the same selling plan
  // group, and the discount comes from the qty-driven volume discount.
  function getVisiblePlans(qty) {
    if (qty < SUB_QTY_RULES.minSub) return [];
    return getSkioPlans();
  }

  // ========== Server cart helpers ==========
  function countServerVariantQty(vid) {
    var sum = 0;
    serverCart.items.forEach(function (it) {
      if (isMainItem(it) && String(it.variant_id) === String(vid)) sum += it.quantity;
    });
    return sum;
  }
  function getServerMainPlan() {
    for (var i = 0; i < serverCart.items.length; i++) {
      var it = serverCart.items[i];
      if (!isMainItem(it)) continue;
      var sp = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan
        ? String(it.selling_plan_allocation.selling_plan.id) : '';
      return sp;
    }
    return null; // no main items in server
  }

  // Per-zone "out of sync" predicates — drive the spinner state via CSS classes.
  function isVariantOutOfSync(vid) {
    return (state.main[vid] || 0) !== countServerVariantQty(vid);
  }
  function isFrequencyOutOfSync() {
    var localPlan = state.freqMode === 'subscribe' ? String(state.planId || '') : '';
    var hasMainInServer = serverCart.items.some(isMainItem);
    if (!hasMainInServer) return false; // nothing to mismatch
    var serverPlan = getServerMainPlan();
    return (serverPlan || '') !== localPlan;
  }
  function isOtherOutOfSync(key) {
    var stateOther = state.other.filter(function (o) { return o.key === key; })[0];
    var serverOther = serverCart.items.filter(function (it) { return it.key === key; })[0];
    if (!stateOther && !serverOther) return false;
    if (!stateOther) return true;
    if (!serverOther) return true;
    return stateOther.qty !== serverOther.quantity;
  }

  // ========== Hydrate from server cart ==========
  // NB: every cart API call goes through fetchTimeout so a stalled response
  // can't lock the drawer indefinitely.
  function fetchCart() {
    return fetchTimeout('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('cart.js HTTP ' + r.status);
        return r.json();
      });
  }
  function hydrateFromServer(cart) {
    // Defensive: guarantee serverCart.items is always an array so every
    // downstream .forEach/.some/.filter call is safe.
    serverCart = cart || serverCart;
    if (!Array.isArray(serverCart.items)) serverCart.items = [];
    // Empty cart → release the landing-page buy-once lock so the next cart starts fresh.
    if ((serverCart.item_count || serverCart.items.length) === 0) { clearBuyonceLock(); clearSaveTiersOverride(); }
    state.main = {};
    state.mainOrder = [];   // variant ids in cart add-order (selected flavors sort first)
    state.other = [];
    // Default to subscribe; the loop below demotes to buy_once if the cart
    // already contains main items WITHOUT a selling_plan (i.e. user previously
    // chose Buy Once and we should respect that). applyRules() then enforces
    // the qty < minSub guard.
    state.freqMode = 'subscribe';
    var detectedPlan = null;
    var sawMainWithoutPlan = false;

    (cart.items || []).forEach(function (it) {
      var sellingPlan = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan
        ? it.selling_plan_allocation.selling_plan.id : null;
      if (isMainItem(it)) {
        state.main[it.variant_id] = (state.main[it.variant_id] || 0) + it.quantity;
        if (state.mainOrder.indexOf(String(it.variant_id)) === -1) state.mainOrder.push(String(it.variant_id));
        if (sellingPlan) {
          if (!detectedPlan) detectedPlan = sellingPlan;
        } else {
          sawMainWithoutPlan = true;
        }
      } else {
        state.other.push({
          key: it.key,
          variantId: it.variant_id,
          qty: it.quantity,
          sellingPlan: sellingPlan,
          properties: it.properties || {},
          title: it.product_title || it.title,
          variantTitle: (it.variant_title && it.variant_title.indexOf('Default') === -1) ? it.variant_title : '',
          image: it.image,
          url: it.url,
          price: it.final_line_price || it.line_price
        });
      }
    });

    state.planId = detectedPlan;
    if (!state.planId) {
      var plans = getSkioPlans();
      if (plans.length) {
        var sorted = plans.slice().sort(function (a, b) { return (a.pct || 0) - (b.pct || 0); });
        state.planId = sorted[0].id;
      }
    }
    // Respect a Buy Once cart — if the cart already has main items and none
    // carry a selling_plan, fall back to buy_once AND lock userPickedFreq so
    // applyRules() doesn't auto-promote it back to subscribe (which would
    // fight the server cart and cause an infinite sync loop). Subscribe
    // carts don't need a lock because applyRules' auto-promote keeps them
    // correct on qty crossings.
    if (sawMainWithoutPlan && !detectedPlan) {
      state.freqMode = 'buy_once';
      state.userPickedFreq = true;
    }
    applyRules();
    render();
    maybeReconcile();
  }

  // Cart-wide all-or-nothing: if ANY main line is subscribed and the cart is
  // subscribe-eligible, make sure EVERY main line is subscribed at the shared
  // plan (auto-convert one-time siblings). Runs after each hydrate so adding a
  // one-time product from its PDP flips the whole cart to subscription without
  // a manual click. Guarded so an un-reconcilable state can't loop forever.
  function maybeReconcile() {
    var hasMain = serverCart.items.some(isMainItem);
    var anyMainSub = serverCart.items.some(function (it) {
      return isMainItem(it) && it.selling_plan_allocation && it.selling_plan_allocation.selling_plan;
    });
    var eligible = getMainTotalQty() >= SUB_QTY_RULES.minSub;

    if (hasMain && anyMainSub && eligible && state.freqMode === 'subscribe' && planMismatch()) {
      var sig = serverCart.items.map(function (it) {
        var sp = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan
          ? it.selling_plan_allocation.selling_plan.id : '0';
        return it.key + ':' + sp + ':' + it.quantity;
      }).join(',');
      if (sig === reconcileSig) { reconcileTries += 1; } else { reconcileSig = sig; reconcileTries = 1; }
      if (reconcileTries > RECONCILE_MAX) { return; }   // give up — never spin forever
      scheduleSync();                                    // doSync() → doRebuild() unifies the plan
    } else {
      reconcileSig = null; reconcileTries = 0;
    }
  }

  // ========== Rules ==========
  // No more pct bands — every frequency plan is interchangeable, so the only
  // job left here is to enforce the "qty<minSub → no subscribe" guard and
  // to make sure state.planId actually points at one of the available plans.
  function applyRules() {
    var totalQty = getMainTotalQty();
    if (totalQty < SUB_QTY_RULES.minSub) {
      // Drop below the subscribe-eligible threshold for any reason — release
      // the Buy Once lock so the next upward crossing auto-promotes back to
      // Subscribe. This mirrors a fresh-start mental model: if the customer
      // shrinks their cart they re-enter the funnel as a new prospect.
      state.userPickedFreq = false;
      if (state.freqMode === 'subscribe') state.freqMode = 'buy_once';
      return;
    }
    var plans = getSkioPlans();
    if (!plans.length) {
      if (state.freqMode === 'subscribe') state.freqMode = 'buy_once';
      return;
    }
    var planIds = plans.map(function (p) { return String(p.id); });
    if (planIds.indexOf(String(state.planId)) === -1) {
      state.planId = plans[0].id;
    }
    // Auto-promote: when qty rises into the subscribe-eligible band and the
    // user hasn't manually chosen Buy Once this session, snap back to
    // Subscribe so the subscribe-first default sticks across qty crossings.
    if (totalQty >= SUB_QTY_RULES.minSub && state.freqMode === 'buy_once' && !state.userPickedFreq) {
      state.freqMode = 'subscribe';
    }
  }

  // ========== Render ==========
  function render() {
    renderFlavorCards();
    renderTierRail();
    renderFrequency();
    renderOtherItems();
    renderTotals();
    renderHeaderBadges();
    renderCheckoutEnabled();
  }

  function renderFlavorCards() {
    var bar = getBar();
    if (!bar) return;
    bar.querySelectorAll('[data-pg-vp-card]').forEach(function (card) {
      var vid = card.getAttribute('data-variant-id');
      var qty = state.main[vid] || 0;
      var addBtn = card.querySelector('[data-pg-vp-add]');
      var qtyWrap = card.querySelector('[data-pg-vp-qty]');
      var valEl = card.querySelector('[data-pg-vp-val]');
      if (qty > 0) {
        card.classList.add('is-selected');
        if (addBtn) addBtn.hidden = true;
        if (qtyWrap) qtyWrap.hidden = false;
        if (valEl) valEl.textContent = String(qty);
      } else {
        card.classList.remove('is-selected');
        if (addBtn) addBtn.hidden = false;
        if (qtyWrap) qtyWrap.hidden = true;
        if (valEl) valEl.textContent = '1';
      }
      card.classList.toggle('is-syncing', isVariantOutOfSync(vid));
    });
    reorderFlavorCards(bar);
  }

  // Float the in-cart flavors to the front of the combined grid, in the order
  // they were added (state.mainOrder). Unselected flavors keep their original
  // product order after. Runs on every render; no-ops when order is unchanged.
  function reorderFlavorCards(bar) {
    var grid = bar.querySelector('[data-pg-vp-grid]');
    if (!grid) return;
    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-pg-vp-card]'));
    if (cards.length < 2) return;
    cards.forEach(function (c, i) {
      if (c.getAttribute('data-pg-vp-ordinal') === null) c.setAttribute('data-pg-vp-ordinal', String(i));
    });
    var order = state.mainOrder || [];
    function ord(c) { return Number(c.getAttribute('data-pg-vp-ordinal')) || 0; }
    var selected = [], rest = [];
    cards.forEach(function (c) {
      if ((state.main[c.getAttribute('data-variant-id')] || 0) > 0) selected.push(c);
      else rest.push(c);
    });
    selected.sort(function (a, b) {
      var ia = order.indexOf(a.getAttribute('data-variant-id')); if (ia === -1) ia = 1e9;
      var ib = order.indexOf(b.getAttribute('data-variant-id')); if (ib === -1) ib = 1e9;
      return ia !== ib ? ia - ib : ord(a) - ord(b);
    });
    rest.sort(function (a, b) { return ord(a) - ord(b); });
    var desired = selected.concat(rest);
    var changed = false;
    for (var j = 0; j < desired.length; j++) {
      if (grid.children[j] !== desired[j]) { changed = true; break; }
    }
    if (!changed) return;
    desired.forEach(function (c) { grid.appendChild(c); });
  }

  function renderTierRail() {
    var bar = getBar();
    if (!bar) return;
    var tiers = Array.prototype.slice.call(bar.querySelectorAll('[data-pg-disc-tier]'));
    if (!tiers.length) return;
    var tierData = tiers.map(function (el) {
      return {
        cells: bar.querySelectorAll('[data-tier-idx="' + el.getAttribute('data-tier-idx') + '"]'),
        qty: parseInt(el.getAttribute('data-tier-qty'), 10) || 0,
        message: el.getAttribute('data-tier-message') || '',
        nextMessage: el.getAttribute('data-tier-next-message') || ''
      };
    }).sort(function (a, b) { return a.qty - b.qty; });
    tierData.forEach(function (t, i) {
      var first = i === 0, last = i === tierData.length - 1;
      t.cells.forEach(function (c) {
        if (first) c.classList.add('pg-disc__cell--first');
        if (last) c.classList.add('pg-disc__cell--last');
      });
    });
    var qty = getMainTotalQty();
    var activeIdx = -1;
    for (var i = 0; i < tierData.length; i++) if (qty >= tierData[i].qty) activeIdx = i;
    tierData.forEach(function (t, i) {
      t.cells.forEach(function (c) {
        c.classList.toggle('is-reached', i < activeIdx);
        c.classList.toggle('is-active', i === activeIdx);
      });
    });
    var progress = bar.querySelector('[data-pg-disc-progress]');
    if (progress && tierData.length > 1) {
      var minQty = tierData[0].qty, maxQty = tierData[tierData.length - 1].qty, pct = 0;
      if (qty <= minQty) pct = 0;
      else if (qty >= maxQty) pct = 100;
      else {
        var nextTier = tierData[activeIdx + 1];
        if (nextTier) {
          var span = nextTier.qty - tierData[activeIdx].qty;
          var into = qty - tierData[activeIdx].qty;
          var stepPct = span > 0 ? (into / span) : 0;
          var perStep = 100 / (tierData.length - 1);
          pct = (activeIdx * perStep) + (stepPct * perStep);
        }
      }
      // Scale so the fill ends at the radio centers (step 1 → 48%, last → 96%)
      // to pair with the 10px left offset on the fill.
      progress.style.width = Math.max(0, Math.min(96, pct * 0.96)) + '%';
    } else if (progress) {
      progress.style.width = activeIdx >= 0 ? '96%' : '0%';
    }
    var msgEl = bar.querySelector('[data-pg-disc-message]');
    if (msgEl) {
      var m = '';
      if (activeIdx >= 0 && tierData[activeIdx].message) m = tierData[activeIdx].message;
      var next = tierData[activeIdx + 1];
      if (next && next.nextMessage) m = next.nextMessage;
      msgEl.textContent = m;
    }
  }

  function renderFrequency() {
    var bar = getBar();
    if (!bar) return;
    var freqEl = bar.querySelector('[data-pg-fr]');
    if (!freqEl) return;
    var totalQty = getMainTotalQty();
    if (totalQty < SUB_QTY_RULES.minSub) {
      freqEl.hidden = true;
      return;
    }
    freqEl.hidden = false;

    // Every plan pill is visible whenever subscribe is allowed — pct gating
    // is gone since the volume discount applies regardless of which frequency
    // the user picks.
    freqEl.querySelectorAll('[data-pg-fr-plan-pill]').forEach(function (pill) {
      var input = pill.querySelector('[data-pg-fr-plan-input]');
      var pillId = input ? input.value : null;
      pill.hidden = false;
      var selected = String(pillId) === String(state.planId);
      if (input) {
        input.disabled = false;
        input.checked = selected;
      }
      pill.classList.toggle('is-selected', selected);
    });

    var buyEl = freqEl.querySelector('[data-pg-fr-opt="buy_once"]');
    var subEl = freqEl.querySelector('[data-pg-fr-opt="subscribe"]');
    if (buyEl) buyEl.classList.toggle('is-selected', state.freqMode === 'buy_once');
    if (subEl) subEl.classList.toggle('is-selected', state.freqMode === 'subscribe');
    var buyInput = buyEl && buyEl.querySelector('input.pg-fr__radio-input');
    var subInput = subEl && subEl.querySelector('input.pg-fr__radio-input');
    if (buyInput) buyInput.checked = state.freqMode === 'buy_once';
    if (subInput) subInput.checked = state.freqMode === 'subscribe';

    // "Switch & Save" upsell banner — visible only in Buy Once mode.
    var switchBanner = drawer.querySelector('[data-pg-switch-banner]');
    if (switchBanner) switchBanner.classList.toggle('is-visible', state.freqMode === 'buy_once');

    // SAVE % badge is driven by the qty-based virtual discount, not the
    // plan, since SKIO plans no longer carry per-plan pcts.
    var activePct = getVirtualSubDiscountPct(totalQty);
    var badgePctEl = freqEl.querySelector('[data-pg-fr-badge-pct]');
    if (badgePctEl) badgePctEl.textContent = String(activePct);

    freqEl.classList.toggle('is-syncing', isFrequencyOutOfSync());
  }

  function renderOtherItems() {
    drawer.querySelectorAll('[data-cd-line]').forEach(function (row) {
      var key = row.getAttribute('data-cd-key');
      var match = state.other.filter(function (it) { return it.key === key; })[0];
      if (!match) {
        row.style.display = 'none';
      } else {
        row.style.display = '';
        var valEl = row.querySelector('[data-cd-other-val]');
        if (valEl) valEl.textContent = String(match.qty);
      }
      row.classList.toggle('is-syncing', isOtherOutOfSync(key));
    });
  }

  function renderTotals() {
    var bar = getBar();
    if (!bar) return;
    var saveTiers = getSaveTiers();
    var freqEl = bar.querySelector('[data-pg-fr]');
    var perShotUnits = freqEl ? (parseInt(freqEl.getAttribute('data-per-shot-units'), 10) || 12) : 12;

    var totalCents = 0, compareCents = 0, anyCompare = false, totalQty = 0;
    bar.querySelectorAll('[data-pg-vp-card]').forEach(function (card) {
      var vid = card.getAttribute('data-variant-id');
      var qty = state.main[vid] || 0;
      if (!qty) return;
      var price = parseInt(card.getAttribute('data-variant-price'), 10) || 0;
      var compare = parseInt(card.getAttribute('data-variant-compare'), 10) || 0;
      totalCents += price * qty;
      totalQty += qty;
      if (compare > price) { compareCents += compare * qty; anyCompare = true; }
      else { compareCents += price * qty; }
    });

    var discounted = totalCents;
    var tierFullPrice = null;
    if (saveTiers && totalQty > 0) {
      var perPack = tierPriceFor(saveTiers, totalQty);
      var fullPack = saveTiers[0];
      if (perPack != null) discounted = perPack * totalQty;
      if (fullPack != null) tierFullPrice = fullPack * totalQty;
    }

    // Virtual subscribe discount — qty 2 → 20%, qty 3+ → 25%. Mirrors the
    // Shopify volume discount that auto-applies on checkout.
    var activePct = getVirtualSubDiscountPct(totalQty);
    var subPriceCents = activePct > 0 ? Math.round(totalCents * (100 - activePct) / 100) : totalCents;

    // A/B pin: the source PDP writes `pg:cart_buyonce_mode` into
    // localStorage on PDP load ('on' = control PDP, 'off' = subscription
    // PDP). Uses localStorage — not sessionStorage — so it persists across
    // tabs to match Intelligems' cookie-based cohort assignment.
    // Cart drawer follows that pin for the rest of the session. When the
    // key is absent (fresh visit, no ATC yet) we default to 'off' — the
    // safer A/B baseline agreed with the merchant.
    var barRootEarly = drawer.querySelector('[data-pg-discount-bar]');
    // The cart drawer's own "Disable Buy-Once discounts" section setting is
    // authoritative — read it straight from the rendered attribute (defaults to
    // enabled when the attribute is '0'/absent). We no longer let the PDP's
    // localStorage pin override the merchant's cart-level toggle.
    var disableBuyonceDiscounts = (!!barRootEarly
      && barRootEarly.getAttribute('data-pg-disable-buyonce-discounts') === '1')
      || buyonceLockActive();   // pinned by a landing page (see BUYONCE_LOCK below)
    var buyoncePrice = disableBuyonceDiscounts ? totalCents : discounted;

    var headingEl = drawer.querySelector('.pg-disc__head-text[data-pg-tier-heading-buyonce]');
    if (headingEl) {
      var subHeading = headingEl.getAttribute('data-pg-tier-heading-sub') || '';
      var buyHeading = headingEl.getAttribute('data-pg-tier-heading-buyonce') || '';
      var nextHeading = disableBuyonceDiscounts ? subHeading : buyHeading;
      if (nextHeading && headingEl.textContent !== nextHeading) {
        headingEl.textContent = nextHeading;
      }
    }

    var freqBuyNow = drawer.querySelector('[data-pg-fr-buyonce-now]');
    var freqSubNow = drawer.querySelector('[data-pg-fr-sub-now]');
    var freqBuyWas = drawer.querySelector('[data-pg-fr-buyonce-was]');
    var freqSubWas = drawer.querySelector('[data-pg-fr-sub-was]');
    var freqBuyPer = drawer.querySelector('[data-pg-fr-buyonce-per]');
    var freqSubPer = drawer.querySelector('[data-pg-fr-sub-per]');
    var freqCompareCents = Math.max(tierFullPrice || 0, anyCompare ? compareCents : 0, totalCents);

    if (freqBuyNow) freqBuyNow.textContent = formatMoney(buyoncePrice);
    if (freqSubNow) freqSubNow.textContent = formatMoney(subPriceCents);
    setWas(freqBuyWas, !disableBuyonceDiscounts && freqCompareCents > buyoncePrice, freqCompareCents);
    setWas(freqSubWas, freqCompareCents > subPriceCents, freqCompareCents);
    var totalShots = totalQty * perShotUnits;
    if (freqBuyPer) freqBuyPer.textContent = totalShots > 0 ? '$' + (buyoncePrice / 100 / totalShots).toFixed(2) + ' / shot' : '/ shot';
    if (freqSubPer) freqSubPer.textContent = totalShots > 0 ? '$' + (subPriceCents / 100 / totalShots).toFixed(2) + ' / shot' : '/ shot';

    // Tier-rail per-shot labels:
    //   - Tier 1 (qty 1): always show the base retail per-shot (no
    //     subscribe discount, no metafield discount).
    //   - Tier 2+ buy-once: show the metafield's per-shot value.
    //   - Tier 2+ subscribe: apply the tiered subscribe ladder
    //     (0%/20%/25%) to the base retail per-shot so the progress
    //     bar mirrors the subscription section price exactly.
    var barRoot = drawer.querySelector('[data-pg-discount-bar]');
    var baseRetailPerShotCents = barRoot ? (parseInt(barRoot.getAttribute('data-pg-base-retail-pershot-cents'), 10) || 0) : 0;
    var isSubMode = state.freqMode === 'subscribe';
    function pctForTierQty(q) {
      if (q >= 3) return 25;
      if (q >= 2) return 20;
      return 0;
    }
    // When an A/B save-tiers override is latched, the buy-once rail per-shot + SAVE
    // badges recompute from those tiers so the cart matches the page it was added from.
    var saveTiersOverridden = !!getSaveTiersOverride();
    var shotsPerPackCart = parseInt(barRootEarly && barRootEarly.getAttribute('data-pg-per-shot-units'), 10) || perShotUnits || 12;
    var pershotEls = drawer.querySelectorAll('[data-pg-disc-pershot]');
    for (var psi = 0; psi < pershotEls.length; psi++) {
      var psEl = pershotEls[psi];
      var savedCents = parseInt(psEl.getAttribute('data-pg-disc-pershot-cents'), 10) || 0;
      var tierQty = parseInt(psEl.getAttribute('data-pg-disc-pershot-qty'), 10) || 1;
      var shownCents;
      if (tierQty <= 1) {
        shownCents = baseRetailPerShotCents || savedCents;
      } else if (isSubMode) {
        var pct = pctForTierQty(tierQty);
        shownCents = Math.round((baseRetailPerShotCents || savedCents) * (100 - pct) / 100);
      } else if (disableBuyonceDiscounts) {
        // Buy-once discounts off → tier rail shows base retail per shot.
        shownCents = baseRetailPerShotCents || savedCents;
      } else if (saveTiersOverridden && saveTiers) {
        // Buy-once with a latched override → per-shot from the override tiers.
        var ovPP = tierPriceFor(saveTiers, tierQty);
        shownCents = (ovPP != null) ? Math.round(ovPP / shotsPerPackCart) : savedCents;
      } else {
        shownCents = savedCents;
      }
      psEl.textContent = '$' + (shownCents / 100).toFixed(2) + ' / shot';
    }

    // LOCKED prefix + subscribe-savings SAVE-badge rewrite. Fires only
    // when buy-once discounts are disabled AND buy-once is the active
    // mode. Cache the server-rendered SAVE text on first paint so the
    // toggle can restore it cleanly when flipped off again.
    var lockBadges = disableBuyonceDiscounts && !isSubMode;
    var perShotUnitsCart = parseInt(barRootEarly && barRootEarly.getAttribute('data-pg-per-shot-units'), 10) || perShotUnits || 12;
    var lockEls = drawer.querySelectorAll('[data-pg-tier-lock]');
    for (var lbi = 0; lbi < lockEls.length; lbi++) {
      lockEls[lbi].hidden = !lockBadges;
    }
    var saveWraps = drawer.querySelectorAll('[data-pg-tier-save]');
    for (var swi = 0; swi < saveWraps.length; swi++) {
      var wrap = saveWraps[swi];
      var textEl = wrap.querySelector('[data-pg-tier-save-text]');
      if (!textEl) continue;
      if (!textEl.dataset.pgOriginal) {
        textEl.dataset.pgOriginal = textEl.textContent.trim();
      }
      if ((isSubMode || disableBuyonceDiscounts) && baseRetailPerShotCents) {
        // Subscribe (or locked Buy Once) → subscribe savings ladder.
        // Buy Once with discounts on → the server-rendered volume savings (pgOriginal).
        var tqty = parseInt(wrap.getAttribute('data-pg-tier-qty'), 10) || 0;
        var extra = wrap.getAttribute('data-pg-tier-extra') || '';
        var isLast = wrap.getAttribute('data-pg-tier-is-last') === '1';
        if (tqty > 1) {
          var subP = pctForTierQty(tqty);
          var retailCents = baseRetailPerShotCents * perShotUnitsCart * tqty;
          var subSaveDollars = Math.round((retailCents * subP / 100) / 100);
          var out = '';
          if (subSaveDollars > 0) out = 'SAVE $' + subSaveDollars + (isLast ? '+' : '');
          if (subSaveDollars > 0 && extra) out += ' ';
          if (extra) out += extra;
          textEl.textContent = out;
        } else {
          textEl.textContent = extra || textEl.dataset.pgOriginal;
        }
      } else if (saveTiersOverridden && saveTiers && baseRetailPerShotCents) {
        // Buy-once with a latched override → recompute the volume SAVE $ from the
        // override tiers: (retail per-pack − override per-pack) × packs, floored.
        var tqO = parseInt(wrap.getAttribute('data-pg-tier-qty'), 10) || 0;
        var exO = wrap.getAttribute('data-pg-tier-extra') || '';
        var lastO = wrap.getAttribute('data-pg-tier-is-last') === '1';
        if (tqO > 1) {
          var retailPP = baseRetailPerShotCents * shotsPerPackCart;
          var ovPP2 = tierPriceFor(saveTiers, tqO);
          var volSaveD = (ovPP2 != null) ? Math.floor(((retailPP - ovPP2) * tqO) / 100) : 0;
          var outO = '';
          if (volSaveD > 0) outO = 'SAVE $' + volSaveD + (lastO ? '+' : '');
          if (volSaveD > 0 && exO) outO += ' ';
          if (exO) outO += exO;
          textEl.textContent = outO;
        } else {
          textEl.textContent = exO || textEl.dataset.pgOriginal;
        }
      } else {
        textEl.textContent = textEl.dataset.pgOriginal;
      }
    }

    var mainFinalCents = (state.freqMode === 'subscribe') ? subPriceCents : buyoncePrice;
    var otherTotal = 0;
    state.other.forEach(function (it) { otherTotal += (it.price || 0); });
    var totalEl = drawer.querySelector('[data-cd-total]');
    var grandCents = mainFinalCents + otherTotal;
    if (totalEl) totalEl.textContent = formatMoney(grandCents);

    var shipEl = drawer.querySelector('[data-cd-shipping]');
    if (shipEl) {
      var threshold = parseFloat(shipEl.getAttribute('data-shipping-threshold')) || 50;
      var thresholdCents = Math.round(threshold * 100);
      var itemCount = totalQty + getOtherTotalQty();
      var qualifies = grandCents >= thresholdCents || itemCount > 1;
      var freeText = shipEl.getAttribute('data-shipping-free') || 'Free shipping';
      var defaultText = shipEl.getAttribute('data-shipping-default') || 'Unlock free shipping at $50';
      shipEl.textContent = qualifies ? freeText : defaultText;
      shipEl.classList.toggle('cd-totals__shipping--free', qualifies);
    }
  }

  function setWas(el, show, cents) {
    if (!el) return;
    if (show) { el.textContent = formatMoney(cents); el.hidden = false; }
    else el.hidden = true;
  }

  function renderHeaderBadges() {
    var count = getMainTotalQty() + getOtherTotalQty();
    document.querySelectorAll('.cart-count').forEach(function (b) {
      b.textContent = count > 0 ? String(count) : '';
    });
  }

  // True whenever there's anything in flight or queued — debounce timer set,
  // a sync currently running, or a follow-up sync queued behind it. Used to
  // gate the Checkout button so the user can't fire a checkout while the
  // server cart is mid-update.
  function isPending() {
    return !!(syncTimer || syncing || pendingSync);
  }
  // On mobile only, smooth-scroll the drawer body so the Subscribe & Stay
  // Stocked block is visible. Triggered when the user's qty crosses the
  // minSub threshold — the savings/benefits drive the conversion, so we
  // surface them automatically instead of relying on the user to scroll.
  function scrollFrequencyIntoViewOnMobile() {
    if (window.innerWidth >= 750) return;
    var freqEl = drawer.querySelector('[data-pg-fr]');
    var body = drawer.querySelector('.cd-drawer__body');
    if (!freqEl || !body) return;
    // rAF: let render's hidden=false / layout commit before we measure.
    requestAnimationFrame(function () {
      if (freqEl.hidden) return;
      var freqRect = freqEl.getBoundingClientRect();
      var bodyRect = body.getBoundingClientRect();
      var target = body.scrollTop + (freqRect.top - bodyRect.top) - 16;
      try {
        body.scrollTo({ top: target, behavior: 'smooth' });
      } catch (e) {
        body.scrollTop = target;
      }
    });
  }

  function renderCheckoutEnabled() {
    var btn = drawer.querySelector('[data-cd-checkout]');
    if (!btn) return;
    var anyQty = getMainTotalQty() > 0 || state.other.length > 0;
    btn.disabled = !anyQty || checkingOut || isPending();
  }

  // ========== Mutations ==========
  function flavorIncrement(vid) {
    var prevQty = getMainTotalQty();
    state.main[vid] = (state.main[vid] || 0) + 1;
    if (!state.mainOrder) state.mainOrder = [];
    if (state.mainOrder.indexOf(String(vid)) === -1) state.mainOrder.push(String(vid));
    applyRules();
    render();
    // When mobile users cross the qty threshold that unlocks the Subscribe
    // option, auto-scroll the freq section into view so they see the savings
    // and benefits without having to scroll manually.
    if (prevQty < SUB_QTY_RULES.minSub && getMainTotalQty() >= SUB_QTY_RULES.minSub) {
      scrollFrequencyIntoViewOnMobile();
    }
    scheduleSync();
  }
  function flavorDecrement(vid) {
    var next = Math.max(0, (state.main[vid] || 0) - 1);
    if (next === 0) {
      delete state.main[vid];
      if (state.mainOrder) {
        var oi = state.mainOrder.indexOf(String(vid));
        if (oi !== -1) state.mainOrder.splice(oi, 1);
      }
    } else {
      state.main[vid] = next;
    }
    applyRules();
    render();
    scheduleSync();
  }
  function setFrequency(mode) {
    if (mode !== 'buy_once' && mode !== 'subscribe') return;
    if (mode === 'subscribe' && getMainTotalQty() < SUB_QTY_RULES.minSub) return;
    state.userPickedFreq = true;
    if (mode === state.freqMode) return;
    state.freqMode = mode;
    applyRules();
    render();
    scheduleSync();
  }
  function setPlan(pid) {
    if (String(pid) === String(state.planId) && state.freqMode === 'subscribe') return;
    state.planId = pid;
    state.freqMode = 'subscribe';
    applyRules();
    render();
    scheduleSync();
  }
  function otherChange(key, delta) {
    var idx = -1;
    for (var i = 0; i < state.other.length; i++) if (state.other[i].key === key) { idx = i; break; }
    if (idx < 0) return;
    state.other[idx].qty = Math.max(0, state.other[idx].qty + delta);
    if (state.other[idx].qty === 0) state.other.splice(idx, 1);
    render();
    scheduleSync();
  }
  function otherRemove(key) {
    state.other = state.other.filter(function (it) { return it.key !== key; });
    render();
    scheduleSync();
  }

  // ========== Sync (debounced background) ==========
  function scheduleSync() {
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(function () { syncTimer = null; runSync(); }, SYNC_DEBOUNCE_MS);
    // Reflect the pending state on the Checkout button immediately, since
    // render() already ran above with the OLD (null) syncTimer.
    renderCheckoutEnabled();
  }

  // Sync. On success, server is authoritative for serverCart; skeleton clears
  // because local state already matches what we just wrote. On failure (or
  // if a fetch hangs past the watchdog), rehydrate from server so the UI
  // snaps back to reality — never leaves the user staring at a stuck skeleton.
  function runSync() {
    if (syncing) { pendingSync = true; return; }
    syncing = true;

    var settled = false;
    var settle = function (rehydrate) {
      if (settled) return;
      settled = true;
      var p = rehydrate
        ? fetchCart().then(hydrateFromServer).catch(function (err) {
            console.error('[cart-drawer] settle rehydrate failed', err);
          })
        : Promise.resolve();
      p.then(function () {
        syncing = false;
        render();
        if (pendingSync) { pendingSync = false; scheduleSync(); }
      });
    };

    var watchdog = setTimeout(function () { if (!settled) settle(true); }, 10000);

    doSync().then(function () {
      clearTimeout(watchdog);
      settle(false);
    }, function (e) {
      clearTimeout(watchdog);
      console.error('[cart-drawer] sync failed', e);
      settle(true);
    });
  }

  function planMismatch() {
    var localPlan = state.freqMode === 'subscribe' ? String(state.planId || '') : '';
    var hasMain = serverCart.items.some(isMainItem);
    if (!hasMain) return false;
    for (var i = 0; i < serverCart.items.length; i++) {
      var it = serverCart.items[i];
      if (!isMainItem(it)) continue;
      var sp = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan
        ? String(it.selling_plan_allocation.selling_plan.id) : '';
      if (sp !== localPlan) return true;
    }
    return false;
  }

  function buildAddItem(vid, qty, includePlan) {
    var item = { id: parseInt(vid, 10), quantity: qty };
    if (includePlan && state.freqMode === 'subscribe' && state.planId) {
      item.selling_plan = parseInt(state.planId, 10);
    }
    return item;
  }

  // Pure quantity sync — touches only line quantities, preserves selling_plan
  // on existing main lines, adds new variants via /cart/add.js. No moment
  // where the cart goes empty.
  //
  // Shopify's /cart/update.js takes EITHER an array (line-index quantities)
  // OR an object keyed by variant id. We need line-based updates because the
  // same variant can appear on multiple lines (with vs without selling_plan)
  // and we want to act on a specific line — so we always send an array.
  function doDeltaSync() {
    var updates = serverCart.items.map(function (it) {
      if (isMainItem(it)) {
        var vid = String(it.variant_id);
        return state.main[vid] || 0;
      }
      var stateOther = state.other.filter(function (o) { return o.key === it.key; })[0];
      return stateOther ? stateOther.qty : 0;
    });
    var coveredMain = {};
    serverCart.items.forEach(function (it) {
      if (isMainItem(it)) coveredMain[String(it.variant_id)] = true;
    });
    var addItems = [];
    Object.keys(state.main).forEach(function (vid) {
      if (!coveredMain[vid] && state.main[vid] > 0) {
        addItems.push(buildAddItem(vid, state.main[vid], true));
      }
    });
    return applyDiff(updates, addItems);
  }

  // Full rebuild — clears every main line and re-adds them under the new
  // mode/plan. Other items pass through with their qty changes. Used only
  // when the selling_plan has actually changed for at least one main line.
  function doRebuild() {
    var updates = serverCart.items.map(function (it) {
      if (isMainItem(it)) return 0;
      var stateOther = state.other.filter(function (o) { return o.key === it.key; })[0];
      return stateOther ? stateOther.qty : 0;
    });
    var addItems = [];
    Object.keys(state.main).forEach(function (vid) {
      if (state.main[vid] > 0) {
        addItems.push(buildAddItem(vid, state.main[vid], true));
      }
    });
    return applyDiff(updates, addItems);
  }

  function applyDiff(updates, addItems) {
    var hasUpdates = Array.isArray(updates) && updates.length > 0;
    var p;
    if (hasUpdates) {
      p = cartUpdate(updates).then(function (cart) {
        if (cart && cart.items) serverCart = cart;
        return cart;
      });
    } else {
      p = Promise.resolve(serverCart);
    }
    return p.then(function () {
      if (!addItems.length) return serverCart;
      return cartAdd(addItems).then(fetchCart).then(function (cart) {
        if (cart && cart.items) serverCart = cart;
        return cart;
      });
    });
  }

  function doSync() {
    return planMismatch() ? doRebuild() : doDeltaSync();
  }

  // ========== AJAX wrappers ==========
  function fetchTimeout(url, opts, ms) {
    ms = ms || 8000;
    if (typeof AbortController === 'undefined') return fetch(url, opts);
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, ms);
    var merged = Object.assign({ signal: controller.signal }, opts || {});
    return fetch(url, merged).then(function (r) { clearTimeout(timer); return r; }, function (e) { clearTimeout(timer); throw e; });
  }
  function cartAdd(items) {
    return fetchTimeout('/cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (j) {
          console.error('[cart-drawer] cart/add ' + r.status, j);
          throw j;
        }, function () {
          throw new Error('cart-add HTTP ' + r.status);
        });
      }
      return r.json();
    });
  }
  function cartUpdate(updates) {
    return fetchTimeout('/cart/update.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) {
          console.error('[cart-drawer] cart/update ' + r.status, j);
          throw j;
        }
        return j;
      }, function () {
        throw new Error('cart-update HTTP ' + r.status);
      });
    });
  }

  // ========== Event delegation ==========
  drawer.addEventListener('click', function (e) {
    var target = e.target;

    if (target.closest('[data-cd-close]')) {
      e.preventDefault(); close(); return;
    }

    if (checkingOut) {
      var blocked = target.closest('[data-pg-vp-add],[data-pg-vp-plus],[data-pg-vp-minus],[data-pg-fr-opt],[data-pg-fr-plan-pill],[data-cd-other-plus],[data-cd-other-minus],[data-cd-other-remove],[data-cd-checkout]');
      if (blocked) { e.preventDefault(); return; }
    }

    // Plan pill BEFORE the outer freq label — pills are nested inside the label.
    var planPill = target.closest('[data-pg-fr-plan-pill]');
    if (planPill && !planPill.hidden) {
      e.preventDefault(); e.stopPropagation();
      var planInput = planPill.querySelector('[data-pg-fr-plan-input]');
      if (planInput && !planInput.disabled) setPlan(planInput.value);
      return;
    }
    var switchBtn = target.closest('[data-pg-switch-banner-btn]');
    if (switchBtn) {
      e.preventDefault();
      setFrequency('subscribe');
      return;
    }
    var freqOpt = target.closest('[data-pg-fr-opt]');
    if (freqOpt) {
      setFrequency(freqOpt.getAttribute('data-pg-fr-opt'));
      return;
    }

    var addBtn = target.closest('[data-pg-vp-add]');
    if (addBtn) {
      e.preventDefault();
      var card = addBtn.closest('[data-pg-vp-card]');
      if (!card || card.getAttribute('data-soldout') === 'true') return;
      flavorIncrement(card.getAttribute('data-variant-id'));
      return;
    }
    var plusBtn = target.closest('[data-pg-vp-plus]');
    if (plusBtn) {
      e.preventDefault(); e.stopPropagation();
      var pcard = plusBtn.closest('[data-pg-vp-card]');
      if (!pcard || pcard.getAttribute('data-soldout') === 'true') return;
      flavorIncrement(pcard.getAttribute('data-variant-id'));
      return;
    }
    var minusBtn = target.closest('[data-pg-vp-minus]');
    if (minusBtn) {
      e.preventDefault(); e.stopPropagation();
      var mcard = minusBtn.closest('[data-pg-vp-card]');
      if (!mcard || mcard.getAttribute('data-soldout') === 'true') return;
      flavorDecrement(mcard.getAttribute('data-variant-id'));
      return;
    }

    var otherPlus = target.closest('[data-cd-other-plus]');
    if (otherPlus) {
      e.preventDefault();
      var orow = otherPlus.closest('[data-cd-line]');
      if (orow) otherChange(orow.getAttribute('data-cd-key'), +1);
      return;
    }
    var otherMinus = target.closest('[data-cd-other-minus]');
    if (otherMinus) {
      e.preventDefault();
      var orow2 = otherMinus.closest('[data-cd-line]');
      if (orow2) otherChange(orow2.getAttribute('data-cd-key'), -1);
      return;
    }
    var otherRm = target.closest('[data-cd-other-remove]');
    if (otherRm) {
      e.preventDefault();
      var orow3 = otherRm.closest('[data-cd-line]');
      if (orow3) otherRemove(orow3.getAttribute('data-cd-key'));
      return;
    }

    var checkout = target.closest('[data-cd-checkout]');
    if (checkout) {
      e.preventDefault();
      if (checkout.disabled) return;
      goToCheckout();
      return;
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
  });

  // ========== Checkout ==========
  // Cart is already in sync. Flush any pending debounce, wait for in-flight
  // sync to complete, then submit the checkout form.
  function flushSync() {
    if (syncTimer) {
      clearTimeout(syncTimer);
      syncTimer = null;
      runSync();
    }
    return new Promise(function (resolve) {
      var attempts = 0;
      var iv = setInterval(function () {
        attempts++;
        if (!syncing && !pendingSync && !syncTimer) {
          clearInterval(iv);
          resolve();
        }
        if (attempts > 100) { clearInterval(iv); resolve(); } // 5s ceiling
      }, 50);
    });
  }
  function goToCheckout() {
    if (checkingOut) return;
    checkingOut = true;
    drawer.classList.add('cd-busy');
    renderCheckoutEnabled();
    flushSync().then(function () {
      // Submit a real form to /cart with name="checkout" — same flow Shopify
      // routes through proper checkout creation (preserves preview sessions).
      var form = document.createElement('form');
      form.action = '/cart'; form.method = 'post'; form.style.display = 'none';
      var btn = document.createElement('button');
      btn.type = 'submit'; btn.name = 'checkout';
      form.appendChild(btn);
      document.body.appendChild(form);
      btn.click();
    });
  }

  // ========== Discount form ==========
  function bindDiscountForm() {
    var form = drawer.querySelector('[data-cd-discount-form]');
    if (!form || form.dataset.cdBound) return;
    form.dataset.cdBound = '1';
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = form.querySelector('[data-cd-discount-input]');
      var code = (input && input.value || '').trim();
      if (!code) return;
      flushSync().then(function () {
        window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/?cart=1';
      });
    });
  }
  bindDiscountForm();

  // ========== Header trigger wiring ==========
  // On templates where UpCart is also live (subscription PDP coexists with
  // UpCart's app embed), UpCart attaches its own click handlers to
  // `.trigger-cart`. We register at the document level in the CAPTURE phase
  // and call stopImmediatePropagation so our drawer wins before UpCart's
  // listener can fire — no two-drawer fight.
  function bindTriggers() {
    document.querySelectorAll('.trigger-cart').forEach(function (el) {
      if (el.dataset.cdBound) return;
      el.dataset.cdBound = '1';
      el.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        open();
      }, true);
    });
  }
  if (!document._cdDocTriggerBound) {
    document._cdDocTriggerBound = true;
    document.addEventListener('click', function (e) {
      var trigger = e.target && e.target.closest && e.target.closest('.trigger-cart');
      if (!trigger) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      open();
    }, true);
  }
  bindTriggers();
  document.addEventListener('cart:refresh', bindTriggers);

  // ========== Public API + PDP integration ==========
  function externalRefresh(force) {
    return fetchCart()
      .then(hydrateFromServer)
      .catch(function (e) { console.error('[cart-drawer] refresh failed', e); })
      .then(function () {
        hideOverlay();
        if (force) open();
      });
  }
  var api = {
    showSideCart: function () { open(); },
    hideSideCart: function () { close(); },
    updateSideCart: function (force) { return externalRefresh(force); }
  };
  window.SideCart = api;
  window.CartDrawer = api;

  if (window.$s) {
    window.$s.addToCartSuccess = function () {
      latchCartLocks();
      hideOverlay();
      externalRefresh(true);
    };
    var origFail = window.$s.addToCartFail;
    window.$s.addToCartFail = function (msg) {
      hideOverlay();
      if (typeof origFail === 'function') { try { origFail(msg); return; } catch (e) {} }
      if (msg) alert(msg);
    };
  }

  // ========== Init ==========
  fetchCart().then(hydrateFromServer).catch(function (e) {
    console.error('[cart-drawer] init failed', e);
  });

  if (/[?&]cart=1\b/.test(location.search)) {
    setTimeout(open, 50);
  }
})();
